// Link previews under a message. Each source gets a card shaped for it: posts (X, Instagram, Reddit) show the
// author and the text; videos (YouTube, TikTok) play in place after a click; anything else is a classic link card.
// Every card has a chevron in its top-right corner that collapses it to one line for me (remembered on this device),
// and, for the message's author and for owners and admins, an × that removes the message's previews for everyone.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Embed } from "@bloom/shared";
import { ChevronIcon, CloseButton, RoundButton } from "./icons.tsx";
import { useCollapse } from "./collapse.ts";
import { removeWithUndo, usePendingRemoval } from "./undo.tsx";
import "./media.css";
import { imgProps } from "../imageMemory.ts";
import { fit, UploadedGif, type OnOpen } from "./Attachments.tsx";
import { formatEmbedTime } from "../embedTime.ts";

const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

function box(w: number | undefined, h: number | undefined, maxW: number, maxH: number) {
  if (!w || !h) return { width: maxW, height: Math.round((maxW * 9) / 16) };
  const s = Math.min(1, maxW / w, maxH / h);
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

/** Box style that keeps the media's shape when a narrow screen squeezes it below its natural width. */
const fill = (size: { width: number; height: number }): React.CSSProperties => ({ width: size.width, maxWidth: "100%", aspectRatio: `${size.width} / ${size.height}`, height: "auto" });

const STAT_LABEL: Record<string, string> = { replies: "💬", reposts: "🔁", likes: "♥" };

/** Largest a picture or video may be drawn: full cards, and smaller inside a quoted post. */
type Max = { w: number; h: number; tallW: number; tallH: number };
const FULL: Max = { w: 400, h: 360, tallW: 280, tallH: 500 };
const NESTED: Max = { w: 376, h: 300, tallW: 220, tallH: 380 };

function Player({ e, onOpen, max = FULL, active = true }: { e: Embed; onOpen: OnOpen; max?: Max; active?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  // Collapsing the card stops the video at once, rather than letting it play on while the card folds away.
  useEffect(() => {
    if (active) return;
    setPlaying(false);
    video.current?.pause();
  }, [active]);
  const v = e.video!;
  const tall = (v.height ?? 0) > (v.width ?? 1);
  const size = box(v.width ?? e.image?.width, v.height ?? e.image?.height, tall ? max.tallW : max.w, tall ? max.tallH : Math.min(max.h, 300));
  const poster = v.poster ?? e.image?.url;
  if (playing && v.iframe) {
    return (
      <div className="embed-media" style={fill(size)}>
        <iframe src={v.iframe} title={e.title ?? e.site} width={size.width} height={size.height} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
      </div>
    );
  }
  if (v.url && !poster) {
    // Nothing to show before it plays: let the video draw its own first frame.
    return (
      <div className="embed-media" style={fill(size)}>
        <video ref={video} src={v.url} width={size.width} height={size.height} controls preload="metadata" playsInline />
      </div>
    );
  }
  if (playing && v.url) {
    return (
      <div className="embed-media" style={fill(size)}>
        <video src={v.url} poster={poster} width={size.width} height={size.height} controls autoPlay playsInline />
      </div>
    );
  }
  return (
    <button type="button" className="embed-media embed-play" style={fill(size)} onClick={() => (v.iframe || v.url ? setPlaying(true) : poster && onOpen(poster))} aria-label={`Play ${e.title ?? "video"}`}>
      {poster && <img src={poster} alt="" {...imgProps(poster)} width={size.width} height={size.height} onError={(ev) => (ev.currentTarget.style.display = "none")} />}
      <span className="play-btn" aria-hidden>
        <svg width="22" height="22" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
      </span>
    </button>
  );
}

function Pictures({ e, onOpen, max = FULL }: { e: Embed; onOpen: OnOpen; max?: Max }) {
  const pics = e.gallery?.length ? e.gallery : e.image ? [e.image] : [];
  if (!pics.length) return null;
  // Every picture in the post opens as one set (the grid shows at most 4; the viewer pages through them all).
  const open = (i: number) => {
    const p = pics[i]!;
    if (pics.length === 1) return onOpen(p.url);
    onOpen(p.url, p.url, { gallery: pics.map((x) => ({ src: x.url, original: x.url })), index: i });
  };
  if (pics.length === 1) {
    const p = pics[0]!;
    const size = box(p.width, p.height, max.w, max.h);
    return (
      <button type="button" className="embed-media" style={fill(size)} onClick={() => open(0)} aria-label="Open image">
        <img src={p.url} alt="" {...imgProps(p.url)} width={size.width} height={size.height} onError={(ev) => ((ev.currentTarget.parentElement as HTMLElement).style.display = "none")} />
      </button>
    );
  }
  return (
    <div className={`embed-gallery n${Math.min(pics.length, 4)}`}>
      {pics.slice(0, 4).map((p, i) => (
        <button key={p.url} type="button" className="embed-media" onClick={() => open(i)} aria-label={`Open image ${i + 1} of ${pics.length}`}>
          <img src={p.url} alt="" {...imgProps(p.url)} onError={(ev) => ((ev.currentTarget.parentElement as HTMLElement).style.display = "none")} />
        </button>
      ))}
    </div>
  );
}

/** "nytimes.com/2026/10/…": the link without its scheme, for the collapsed line. */
function shortUrl(url: string) {
  try {
    const u = new URL(url);
    return (u.host.replace(/^www\./, "") + u.pathname.replace(/\/$/, "") + u.search).slice(0, 200);
  } catch {
    return url;
  }
}

/**
 * A post's text: 4 lines until it is opened. "Show more" appears only when the text really is longer than 4 lines
 * (measured: the clamp hides some of it), and clicking the text does the same as the button. Per message on this
 * device, not saved. The list measures its rows, so a row that grows is handled there (MessageList's observer).
 */
function PostText({ text, quoted }: { text: string; quoted?: boolean }) {
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      if (!open) setCut(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, open]);
  const toggle = () => setOpen((o) => !o);
  const expandable = cut || open;
  return (
    <>
      <div
        ref={box}
        className={`embed-desc post${quoted ? " quoted" : ""}${open ? " open" : ""}${expandable ? " expandable" : ""}`}
        onClick={() => {
          if (expandable && window.getSelection()?.isCollapsed !== false) toggle();
        }}
      >
        {text}
      </div>
      {expandable && (
        <button type="button" className="embed-more" aria-expanded={open} onClick={toggle}>
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}

function Card({ e, messageId, onOpen, onRemove }: { e: Embed; messageId: string; onOpen: OnOpen; onRemove?: () => void }) {
  const isPost = !!e.author?.handle || e.provider === "x";
  // Small pictures on plain link cards sit beside the text; big ones go underneath.
  const small = !isPost && !e.video && !!e.image && (e.image.width ?? 0) > 0 && (e.image.width ?? 0) < 400;
  const c = useCollapse(`${messageId}|${e.url}`);
  const label = e.title ?? e.author?.name ?? e.site;
  return (
    <div className={`embed embed-${e.provider}${c.collapsed ? " is-collapsed" : ""}`} style={e.color ? { borderLeftColor: e.color } : undefined}>
      <div className="embed-head">
        <div className="embed-site">
          {e.siteIcon && <img src={e.siteIcon} alt="" width={14} height={14} onError={(ev) => (ev.currentTarget.style.display = "none")} />}
          {isPost ? (
            <a className="embed-site-name embed-site-link" href={e.url} target="_blank" rel="noreferrer noopener" title="Open the post">
              {e.site}
            </a>
          ) : (
            <span className="embed-site-name">{e.site}</span>
          )}
          {c.collapsed && (
            <a className="embed-line" href={e.url} target="_blank" rel="noreferrer noopener" title={e.url}>
              {label !== e.site && <span className="embed-line-title">{label}</span>}
              <span className="embed-line-url">{shortUrl(e.url)}</span>
            </a>
          )}
        </div>
        <div className="embed-controls">
          <RoundButton size="sm" className="collapse-btn" aria-expanded={!c.collapsed} aria-label={c.collapsed ? "Expand preview" : "Collapse preview"} title={c.collapsed ? "Expand" : "Collapse"} onClick={c.toggle}>
            <ChevronIcon />
          </RoundButton>
          {onRemove && <CloseButton size="sm" aria-label="Remove previews for everyone" title="Remove for everyone" onClick={onRemove} />}
        </div>
      </div>
      <div className={c.bodyClass}>
        <div className="collapse-inner">{c.mounted && <CardBody e={e} isPost={isPost} small={small} onOpen={onOpen} active={!c.collapsed} />}</div>
      </div>
    </div>
  );
}

function CardBody({ e, isPost, small, onOpen, active }: { e: Embed; isPost: boolean; small: boolean; onOpen: OnOpen; active: boolean }) {
  return (
    <div className="embed-body">
      <div className="embed-row">
        <div className="embed-text">
          {e.author && (
            <a className="embed-author" href={e.author.url ?? e.url} target="_blank" rel="noreferrer noopener">
              {e.author.avatar && <img src={e.author.avatar} alt="" width={20} height={20} onError={(ev) => (ev.currentTarget.style.display = "none")} />}
              <strong>{e.author.name}</strong>
              {e.author.handle && <span>{e.author.handle}</span>}
            </a>
          )}
          {e.title && (
            <a className="embed-title" href={e.url} target="_blank" rel="noreferrer noopener">
              {e.title}
            </a>
          )}
          {e.description && (isPost ? <PostText text={e.description} /> : <div className="embed-desc">{e.description}</div>)}
        </div>
        {small && (
          <button type="button" className="embed-thumb" onClick={() => onOpen(e.image!.url)} aria-label="Open image">
            <img src={e.image!.url} alt="" {...imgProps(e.image!.url)} onError={(ev) => ((ev.currentTarget.parentElement as HTMLElement).style.display = "none")} />
          </button>
        )}
      </div>
      {e.video ? <Player e={e} onOpen={onOpen} active={active} /> : !small && <Pictures e={e} onOpen={onOpen} />}
      {e.quote && <Quote q={e.quote} onOpen={onOpen} active={active} />}
      {(e.stats || e.ts) && (
        <div className="embed-foot">
          {e.stats &&
            Object.entries(e.stats).map(([k, n]) => (
              <span key={k} title={k}>
                {STAT_LABEL[k] ?? k} {compact.format(n)}
              </span>
            ))}
          {e.ts &&
            (isPost ? (
              <a href={e.url} target="_blank" rel="noreferrer noopener" title="Open the post">
                {formatEmbedTime(e.ts)}
              </a>
            ) : (
              <span>{formatEmbedTime(e.ts)}</span>
            ))}
        </div>
      )}
    </div>
  );
}

/** A quoted post inside a post card (a quote tweet), with its own pictures or video, like X shows it. */
function Quote({ q, onOpen, active }: { q: Embed; onOpen: OnOpen; active: boolean }) {
  return (
    <div className="embed-quote">
      <a className="embed-quote-head" href={q.url} target="_blank" rel="noreferrer noopener">
        {q.author?.avatar && <img src={q.author.avatar} alt="" width={18} height={18} onError={(ev) => (ev.currentTarget.style.display = "none")} />}
        {q.author && <strong>{q.author.name}</strong>}
        {q.author?.handle && <span>{q.author.handle}</span>}
        {q.ts && <span>· {formatEmbedTime(q.ts)}</span>}
      </a>
      {q.description && <PostText text={q.description} quoted />}
      {q.video ? <Player e={q} onOpen={onOpen} max={NESTED} active={active} /> : <Pictures e={q} onOpen={onOpen} max={NESTED} />}
    </div>
  );
}

/**
 * A link straight to a picture or GIF (#688): the picture alone, drawn as an uploaded picture is (the same box, the same
 * corner controls, the same GIF plays setting), with no card frame, site line or title. The room read its size from the
 * file's header, so the box is its final size before the picture loads. It loads through /api/img.
 */
function ImageLink({ e, messageId, onOpen, onRemove }: { e: Embed; messageId: string; onOpen: OnOpen; onRemove?: () => void }) {
  const c = useCollapse(`${messageId}|${e.url}`);
  const img = e.image!;
  const box = fit(img.width, img.height, 400, 320);
  const name = e.site;
  return (
    <div className={`att visual embed-image${c.collapsed ? " is-collapsed" : ""}`}>
      {c.collapsed && (
        <div className="att-line">
          <span className="att-line-kind">{img.type === "image/gif" ? "GIF" : "Image"}</span>
          <a className="att-line-name" href={e.url} target="_blank" rel="noreferrer noopener" title={e.url}>
            {shortUrl(e.url)}
          </a>
        </div>
      )}
      <div className={c.bodyClass}>
        <div className="collapse-inner">
          {c.mounted && (
            <div className="att-media">
              {img.type === "image/gif" ? (
                <UploadedGif name={name} url={img.url} box={box} onOpen={onOpen} hidden={false} />
              ) : (
                <button type="button" className="media" style={box} onClick={() => onOpen(img.url, img.url)} aria-label={`Open image from ${name}`}>
                  <img src={img.url} alt="" {...imgProps(img.url)} width={box.width} height={box.height} onError={(ev) => ((ev.currentTarget.parentElement as HTMLElement).style.display = "none")} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`att-controls${c.collapsed ? "" : " over"}`}>
        <RoundButton size="sm" tone={c.collapsed ? "plain" : "media"} className="collapse-btn" aria-expanded={!c.collapsed} aria-label={c.collapsed ? "Expand image" : "Collapse image"} title={c.collapsed ? "Expand" : "Collapse"} onClick={c.toggle}>
          <ChevronIcon />
        </RoundButton>
        {onRemove && <CloseButton size="sm" tone={c.collapsed ? "plain" : "media"} aria-label="Remove previews for everyone" title="Remove for everyone" onClick={onRemove} />}
      </div>
    </div>
  );
}

/**
 * onSuppress: removes this message's previews for everyone (given only to the author and to owners and admins).
 * It's sent after a few seconds' chance to undo; until then the previews are just hidden here.
 */
export function Embeds({ messageId, embeds, onOpen, onSuppress }: { messageId: string; embeds: Embed[]; onOpen: OnOpen; onSuppress?: () => void }) {
  const id = `embeds:${messageId}`;
  const removing = usePendingRemoval(id);
  if (removing) return null;
  const remove = onSuppress && (() => removeWithUndo(id, embeds.length > 1 ? "Previews removed" : "Preview removed", onSuppress));
  return (
    <div className="embeds">
      {embeds.map((e) =>
        e.provider === "image" && e.image ? <ImageLink key={e.url} e={e} messageId={messageId} onOpen={onOpen} onRemove={remove} /> : <Card key={e.url} e={e} messageId={messageId} onOpen={onOpen} onRemove={remove} />,
      )}
    </div>
  );
}
